import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from '@common/hooks/useDebounce';

const DEBOUNCE_DELAY = 300;

export function useAsyncOptions({
  loadOptions,
  filterType,
  cachedOptions,
  searchInputValue = '',
}) {
  const [options, setOptions] = useState(cachedOptions || []);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(!!cachedOptions);
  const lastSearchTerm = useRef('');

  const debouncedSearchTerm = useDebounce(searchInputValue, DEBOUNCE_DELAY);

  const syncCachedOptions = () => {
    if (cachedOptions) {
      setOptions(cachedOptions);
      setHasLoaded(true);
    }
  };

  const resetOnFilterTypeChange = () => {
    if (!cachedOptions) {
      setHasLoaded(false);
      setOptions([]);
    }
  };

  useEffect(syncCachedOptions, [cachedOptions]);
  useEffect(resetOnFilterTypeChange, [filterType, cachedOptions]);

  const fetchOptions = useCallback(
    async (searchTerm = '') => {
      if (!loadOptions || isLoading) return;

      setIsLoading(true);
      try {
        const loadedOptions = await loadOptions(filterType, searchTerm);
        setOptions(loadedOptions || []);
        setHasLoaded(true);
        lastSearchTerm.current = searchTerm;
      } catch (error) {
        console.error('Failed to load options:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadOptions, filterType],
  );

  // Fetch options when debounced search term changes (for dynamic search)
  useEffect(() => {
    if (
      debouncedSearchTerm &&
      debouncedSearchTerm !== lastSearchTerm.current &&
      !cachedOptions
    ) {
      fetchOptions(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, cachedOptions, fetchOptions]);

  const handleMenuOpen = useCallback(() => {
    if (!hasLoaded && !cachedOptions) {
      fetchOptions('');
    }
  }, [hasLoaded, cachedOptions, fetchOptions]);

  return { options, isLoading, handleMenuOpen };
}
